<template>
  <AppModal
    :show="true"
    @close="$emit('close')"
    :title="$t('documents.uploadingFile')"
    :subtitle="fileName || 'Processing your file...'"
    icon="upload"
    size="medium"
    :closable="false"
    :overlay-click-close="false"
  >    <!-- Progress section -->
    <div class="file-upload-content">
      <div class="file-upload-progress">
        <!-- Progress bar -->
        <div class="file-upload__progress-bar">
          <div 
            class="file-upload__progress-fill" 
            :style="{ width: `${progress}%` }"
            :class="{ 'file-upload__progress-fill--complete': progress >= 100 }"
            role="progressbar"
            :aria-valuenow="progress"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-label="`Upload progress: ${progress}%`"
          ></div>
        </div>
        
        <!-- Progress info -->
        <div class="file-upload__progress-info">
          <span class="file-upload__progress-percent">{{ progress }}%</span>
          <div class="file-upload__progress-status">
            <svg v-if="progress < 100" class="file-upload__progress-icon file-upload__progress-icon--spin" viewBox="0 0 24 24">
              <path fill="currentColor" d="M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z"/>
            </svg>
            <svg v-else class="file-upload__progress-icon file-upload__progress-icon--success" viewBox="0 0 24 24">
              <path fill="currentColor" d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z"/>
            </svg>
            {{ progress < 100 ? $t('documents.uploading') : $t('documents.uploadComplete') }}
          </div>
        </div>
        
        <!-- File size info if available -->
        <div v-if="fileSize" class="file-upload__file-info">
          <span class="file-upload__file-size">
            {{ formatFileSize(fileSize) }}
          </span>
          <span v-if="uploadSpeed" class="file-upload__upload-speed">
            {{ uploadSpeed }}/s
          </span>
        </div>
      </div>
    </div>
  </AppModal>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { useI18n } from 'vue-i18n';
import AppModal from '../../base/Modal.vue';

const props = defineProps<{
  progress: number;
  fileName?: string;
  fileSize?: number;
  uploadSpeed?: string;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { t } = useI18n();

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';

  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size = size / 1024;
    unitIndex++;
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`;
};
</script>

<style scoped>
/* ==========================================================================
   FILE UPLOAD MODAL STYLES
   ========================================================================== */

.file-upload-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
}

.file-upload-progress {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

/* Progress bar styles */
.file-upload__progress-bar {
  width: 100%;
  height: 8px;
  background: var(--surface-secondary);
  border-radius: var(--radius-full);
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
}

.file-upload__progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--primary) 0%, var(--primary-400) 100%);
  border-radius: var(--radius-full);
  transition: width var(--transition-slow) ease-out;
  position: relative;
  overflow: hidden;
}

.file-upload__progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: progress-shimmer 1.5s infinite;
}

@keyframes progress-shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

.file-upload__progress-fill--complete {
  background: linear-gradient(90deg, var(--success) 0%, var(--success-400) 100%);
}

/* Progress info styles */
.file-upload__progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: var(--space-md);
}

.file-upload__progress-percent {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--primary);
}

.file-upload__progress-fill--complete + .file-upload__progress-info .file-upload__progress-percent {
  color: var(--success);
}

.file-upload__progress-status {
  display: flex;
  align-items: center;
  gap: var(--space-xs);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
}

.file-upload__progress-icon {
  width: 16px;
  height: 16px;
}

.file-upload__progress-icon--spin {
  color: var(--primary);
  animation: spin 1s linear infinite;
}

.file-upload__progress-icon--success {
  color: var(--success);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* File info styles */
.file-upload__file-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: var(--space-sm);
  margin-top: var(--space-sm);
  border-top: 1px solid var(--border-light);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.file-upload__file-size {
  font-weight: var(--font-weight-medium);
}

.file-upload__upload-speed {
  font-weight: var(--font-weight-medium);
  color: var(--primary);
}

/* ==========================================================================
   RESPONSIVE DESIGN
   ========================================================================== */

@media (max-width: 640px) {
  .file-upload__progress-info {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-xs);
  }
  
  .file-upload__progress-percent {
    text-align: left;
    min-width: auto;
  }
  
  .file-upload__file-info {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-xs);
  }
}

/* ==========================================================================
   ACCESSIBILITY AND MOTION
   ========================================================================== */

@media (prefers-reduced-motion: reduce) {
  .file-upload__progress-fill::after,
  .file-upload__progress-icon--spin {
    animation: none;
  }
  
  .file-upload__progress-fill {
    transition: none;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .file-upload__progress-bar {
    border: 1px solid currentColor;
  }
  
  .file-upload__progress-fill {
    border: 1px solid currentColor;
  }
}
</style>
